

import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Lock, Users, Globe } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api/client';

type AccessDeniedReason = 'invite_required' | 'members_only' | 'region_restricted' | 'not_authenticated';

interface AccessRequestOverlayProps {
    streamId: number;
    streamTitle: string;
    reason?: AccessDeniedReason;
    regionAllowed?: boolean;
}

export function AccessRequestOverlay({ streamId, streamTitle, reason = 'invite_required', regionAllowed = true }: AccessRequestOverlayProps) {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [hasRequested, setHasRequested] = useState(false);
    const [accessDetails, setAccessDetails] = useState<{ restrictions: any } | null>(null);

    useEffect(() => {
        const fetchAccessDetails = async () => {
            try {
                const response = await api.get(`/api/streams/${streamId}/access/check`);
                if (response.success && response.data) {
                    setAccessDetails(response.data as any);
                }
            } catch (e) {
                console.warn('Failed to fetch access details:', e);
            }
        };
        fetchAccessDetails();
    }, [streamId]);

    const handleRequest = async () => {
        setIsSubmitting(true);
        try {
            const response = await api.post(`api/streams/${streamId}/access-request`, {
                reason: 'Requesting access to view'
            });

            if (response.success) {
                setHasRequested(true);
                toast.success('Access request submitted');
            } else {
                toast.error(response.error?.message || 'Failed to submit request');
            }
        } catch (e) {
            console.error('Access request error:', e);
            toast.error('Network error');
        } finally {
            setIsSubmitting(false);
        }
    };

    const getReasonDisplay = () => {
        switch (reason) {
            case 'not_authenticated':
                return {
                    icon: <Lock className="w-10 h-10 text-slate-400" />,
                    title: 'Sign In Required',
                    message: 'You need to sign in to access this content. Please sign in or create an account.',
                    actionLabel: 'Sign In',
                    actionHref: '/auth/signin'
                };
            case 'members_only':
                return {
                    icon: <Users className="w-10 h-10 text-slate-400" />,
                    title: 'Members Only',
                    message: 'This content is available to members only. Become a member to access this stream.',
                    actionLabel: 'Become a Member',
                    actionHref: '/membership'
                };
            case 'region_restricted':
                return {
                    icon: <Globe className="w-10 h-10 text-slate-400" />,
                    title: 'Region Restricted',
                    message: regionAllowed 
                        ? 'This content is not available in your region.'
                        : 'This content is not available in your region. Please contact the content owner for access.',
                    actionLabel: null,
                    actionHref: null
                };
            case 'invite_required':
            default:
                return {
                    icon: <Lock className="w-10 h-10 text-slate-400" />,
                    title: 'Invite Required',
                    message: 'This content requires an invitation. Request access below or enter an invite code if you have one.',
                    actionLabel: 'Request Access',
                    actionHref: null
                };
        }
    };

    const reasonDisplay = getReasonDisplay();

    return (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-sm">
            <div className="text-center max-w-md p-6 space-y-4">
                <div className="bg-slate-800/50 p-4 rounded-full w-20 h-20 mx-auto flex items-center justify-center mb-4">
                    {reasonDisplay.icon}
                </div>

                <h3 className="text-2xl font-bold text-white">{reasonDisplay.title}</h3>
                <p className="text-slate-300">
                    {reasonDisplay.message}
                </p>

                {hasRequested ? (
                    <div className="bg-blue-500/20 text-blue-200 border border-blue-500/50 p-3 rounded-md">
                        Request Pending Approval. You will be notified when access is granted.
                    </div>
                ) : (
                    <>
                        {reasonDisplay.actionLabel && reasonDisplay.actionHref && (
                            <a href={reasonDisplay.actionHref}>
                                <Button
                                    size="lg"
                                    className="w-full font-semibold"
                                >
                                    {reasonDisplay.actionLabel}
                                </Button>
                            </a>
                        )}
                        {reason === 'invite_required' && (
                            <Button
                                size="lg"
                                onClick={handleRequest}
                                disabled={isSubmitting}
                                className="w-full font-semibold"
                            >
                                {isSubmitting ? 'Submitting...' : 'Request Access'}
                            </Button>
                        )}
                        {reasonDisplay.actionLabel && !reasonDisplay.actionHref && reason !== 'invite_required' && (
                            <Button
                                size="lg"
                                onClick={handleRequest}
                                disabled={isSubmitting}
                                className="w-full font-semibold"
                            >
                                {isSubmitting ? 'Submitting...' : reasonDisplay.actionLabel}
                            </Button>
                        )}
                    </>
                )}

                <p className="text-xs text-slate-500 mt-4">
                    Stream: {streamTitle}
                </p>
            </div>
        </div>
    );
}
